import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import PersonalizeDataGrid from "../../../Components/Table/PersonalizeDataGrid";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { Configurations } from "../FormConfigurations";
import { buildListPersonalizeColumns } from "../../../Utils/buildListPersonalizeColumns";

function resolveOrganizationId(userData) {
  return (
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null
  );
}

/** Extract row array from common EMS list API response shapes. */
function extractApiListRows(payload) {
  if (payload == null || typeof payload !== "object") return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  for (const value of Object.values(payload)) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.data)) {
      return value.data;
    }
  }
  return [];
}

export default function ListPersonalizeView() {
  const location = useLocation();
  const { userData } = useAuthStore();
  const orgId = resolveOrganizationId(userData);
  const [previewRows, setPreviewRows] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const baseRoute = useMemo(
    () => location.pathname.replace(/\/personalize-view\/?$/, "") || "/",
    [location.pathname]
  );

  const moduleEntry = useMemo(
    () => Configurations.find((item) => item.route === baseRoute),
    [baseRoute]
  );

  const listProps = moduleEntry?.ListProps;

  const defaultColumns = useMemo(() => {
    if (!listProps) return [];
    return buildListPersonalizeColumns(
      listProps.config,
      listProps.personalizeColumnKeys
    );
  }, [listProps]);

  const datagridKey = listProps?.datagridKey || `${listProps?.tableName || ""}_grid`;
  const filterModule = listProps?.filterModule || listProps?.tableName;

  useEffect(() => {
    if (!listProps?.apiEndpoint || !orgId) {
      setPreviewRows([]);
      return;
    }

    let cancelled = false;

    const loadPreviewRows = async () => {
      setPreviewError(null);
      setPreviewRows(null);

      try {
        const params = new URLSearchParams({
          per_page: "all",
          organization_id: String(orgId),
        });
        const response = await fetch(
          `${MAIN_URL}${listProps.apiEndpoint}?${params.toString()}`,
          {
            method: "GET",
            headers: getApiHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(`Preview fetch failed (${response.status})`);
        }

        const payload = await response.json();
        let rows = extractApiListRows(payload);

        if (typeof listProps.mapResponseToRows === "function") {
          rows = listProps.mapResponseToRows(Array.isArray(rows) ? rows : []);
        }

        if (!cancelled) {
          setPreviewRows(Array.isArray(rows) ? rows : []);
        }
      } catch (error) {
        console.error("Personalize preview fetch error:", error);
        if (!cancelled) {
          setPreviewError(error?.message || "Failed to load preview data");
          setPreviewRows([]);
        }
      }
    };

    loadPreviewRows();

    return () => {
      cancelled = true;
    };
  }, [listProps, orgId]);

  if (!listProps?.tableName) {
    return <Navigate to={baseRoute} replace />;
  }

  if (!orgId || previewRows === null) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <p className="ems-pv-intro">
        <b>{listProps.title || listProps.tableName}</b> — personalize columns, order, width, and
        visibility. Changes save automatically for your organization.
      </p>
      {previewError ? (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: -1.5, mb: 1.5 }}>
          Preview data could not be loaded: {previewError}
        </Typography>
      ) : null}
      <PersonalizeDataGrid
        tableName={listProps.tableName}
        displayTitle={listProps.title || listProps.tableName}
        datagridKey={datagridKey}
        filterModule={filterModule}
        data={previewRows}
        contextId={datagridKey}
        orgId={orgId}
        defaultColumns={defaultColumns}
      />
    </>
  );
}
